import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLocalisation } from '@archipro-website/localisation';
import { EditorialArticleGrid } from './EditorialArticleGrid';
import { EditorialViewMore } from './EditorialViewMore';
import type { MagazineEdition } from './MagazineEditionCarousel';
import { MagazineEditionCarousel } from './MagazineEditionCarousel';
import { PremiumArticleModule } from './PremiumArticleModule';
import subscribeTileImage from '../images/subscribe-tile.jpg';
import { subscribeTileBody } from '../util/editorial-copy';
import { useDirectoryViewMore } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { ARTICLES_LOAD_MORE_API_ROUTE } from '~/routes/remix-api.articles.load-more';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { EditorialCategoryHero } from './EditorialCategoryHero';
import { EditorialFilterBar } from './EditorialFilterBar';
import type { CategoryNavItem } from '../util/derive-category-rail';
import { deriveCategoryRail } from '../util/derive-category-rail';
import type { ArchizeenTile } from '../util/map-archizeen-tile-to-editorial';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import { useArticlesFilterSort } from '../hook/use-articles-filter-sort';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { buildOrderGroup } from '../util/order-groups';
import { EditorialEmptyResults } from './EditorialEmptyResults';
import {
    DirectoryFilterDrawerProvider,
    useDirectoryFilterOpenState,
} from '~/ui-rocco/pages/directory-browse/directory-filter-drawer';

interface ArticleCategoryRevampProps {
    articles: ArchizeenTile[];
    /** Total across all pages, so the grid knows when to stop paging. */
    totalCount: number;
    /** The page size the loader served — the films category serves 18, not 36. */
    pageSize?: number;
    /** Latest issue of each magazine series, for the edition carousel. */
    editions?: MagazineEdition[];
    navItems: CategoryNavItem[];
    /** Falls back to the nav title when the category is missing from the tree. */
    title?: string;
    heroImage?: string;
    /** Facet states for the listing, from the route loader. */
    filterStates: FilterStates;
}

/**
 * Composes the revamped /articles/{category} template.
 *
 * A distinct template from the directory landing page rather than a filtered
 * variant of it: a shorter centred masthead, the category pill rail, and the
 * editorial grid.
 */
export const ArticleCategoryRevamp = ({
    articles,
    totalCount,
    pageSize,
    editions = [],
    navItems,
    title,
    heroImage,
    filterStates,
}: ArticleCategoryRevampProps) => {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { country } = useLocalisation();
    const { searchFilters } = useArticlesFilterSort(filterStates);
    // Persisted per directory, so the panel survives a trip into an article and
    // back — the same rule the other browse pages follow.
    const { open: filtersOpen, toggle: toggleFilters } =
        useDirectoryFilterOpenState('articles');
    const orderGroup = useMemo(
        () => buildOrderGroup(pathname, search),
        [pathname, search],
    );

    const rail = useMemo(
        () => deriveCategoryRail(navItems, pathname),
        [navItems, pathname],
    );

    const initialTiles = useMemo(
        () =>
            articles.map((article) => ({
                model: mapArchizeenTileToEditorial(article),
            })),
        [articles],
    );

    // The query string carries the active facets, so paging has to inherit it —
    // otherwise page two would arrive unfiltered. It travels through the hook's
    // `filters` slot, which also makes it part of the hook's result-set
    // identity: a facet change resets the accumulated pages, as it should.
    const filters = useMemo(() => ({ query: search }), [search]);

    const {
        products: paged,
        loadMore,
        hasMore,
        isLoading,
        nextPage,
    } = useDirectoryViewMore({
        initialProducts: initialTiles,
        totalCount,
        categoryLink: pathname,
        pageSize,
        filters,
        actionPath: ARTICLES_LOAD_MORE_API_ROUTE,
    });

    const tiles = useMemo(
        () => paged.map((tile) => tile.model as ArticleTileViewModel),
        [paged],
    );

    return (
        <main
            // Contains the category masthead's full-bleed breakout — see the
            // directory page for why 100vw overhangs the viewport.
            className="flex flex-col overflow-x-clip pb-[10rem]"
            data-testid="article-category-revamp"
        >
            <EditorialCategoryHero
                title={rail.active?.title ?? title ?? 'Articles'}
                // Categories without a top-read article still need a backdrop,
                // so fall back to the first article in the grid.
                imageSrc={heroImage ?? tiles[0]?.image.src}
            />
            {/* Inter-section rhythm from the Figma frame: 41.96px from the
                masthead to the rail, 72px from the rail to the grid. */}
            <DirectoryFilterDrawerProvider
                value={{
                    open: filtersOpen,
                    toggle: toggleFilters,
                    hasSidebar: true,
                }}
            >
                <EditorialFilterBar
                    items={rail.items}
                    active={rail.active}
                    searchFilters={searchFilters}
                    className="mt-[2.6225rem]"
                />
                <CollapsiblePageLayout
                    sidePanel={
                        <DirectoryFilterSidebar
                            searchFilters={searchFilters}
                            sortOptions={[]}
                            hiddenSectionTitles={[]}
                            leadingGroups={[orderGroup]}
                            // Three short sections: collapsing all but the
                            // first would hide most of what can be filtered.
                            expandAllSections
                        />
                    }
                    hideSidePanel={!filtersOpen}
                    disableMinHeight
                    paddingClassName="px-0"
                >
                    {/*
                     * No top margin on the grid: the filter bar above closes
                     * with its own padding and the products, projects and
                     * professionals listings all start their grid on it. The
                     * empty state keeps a little, being a line of text rather
                     * than a row of images.
                     */}
                    {tiles.length === 0 ? (
                        <EditorialEmptyResults
                            className="mt-[3rem]"
                            // Straight to the bare category: the facets all
                            // live in the query string, so dropping it is the
                            // whole reset — and it keeps the reader on the
                            // category they were reading.
                            onClearFilters={() =>
                                navigate(pathname, {
                                    preventScrollReset: true,
                                })
                            }
                        />
                    ) : (
                        <EditorialArticleGrid articles={tiles} />
                    )}

                    <EditorialViewMore
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onLoadMore={loadMore}
                        nextPage={nextPage}
                        className="mt-[4.9375rem]"
                    />
                </CollapsiblePageLayout>
            </DirectoryFilterDrawerProvider>

            {/*
             * Figma rhythm for the closing sections: 190 to the subscribe
             * module, 160 to the magazine carousel.
             *
             * Explore more sits between them in the design and is built, but is
             * not mounted: its cards are drawn as full-bleed category
             * photographs and nothing supplies one, so they render as three
             * black boxes carrying only a label. It goes back in once a
             * category can be asked for an image to stand behind its name.
             */}

            {/* The same module as the directory's premium feature, carrying
                subscribe copy — which is what the design does here. */}
            {/*
             * A standing promotion, not an article — so its image is the
             * design's own asset rather than whatever the category happens to
             * hold. Copy is the design's, which wraps to four lines in the 805
             * column at 33px.
             */}
            <PremiumArticleModule
                article={{
                    title: 'Want more of this delivered to your inbox?',
                    link: '/digital-magazines',
                    intro: subscribeTileBody(country),
                    imageSrc: subscribeTileImage,
                }}
                eyebrow="Digital publication"
                ctaLabel="Subscribe (Free)"
                variant="subscribe"
                imageBundled={true}
                className="mt-[11.875rem]"
            />

            <MagazineEditionCarousel
                editions={editions}
                className="mt-[10rem]"
            />
        </main>
    );
};
